import { FC, PropsWithChildren, useMemo, useRef, useState, useCallback, useContext, useEffect } from 'react';

import { Form, message } from 'antd';

import { HeaderTabTheftInsurance } from '../components/TabsPropertyDataTheftInsurance/components';
import { TabsPropertyDataTheftInsurance } from '../components';

import QuotationService from '../../../../../../../../shared/services/quotation';

import { GenericDataFormContext, QuotationInfoContext } from '../../../../shared/contexts';
import { StepTwoFormContext } from './StepTwoFormContext';

import { Products, SelectOption } from '../../../../../../../../shared/interfaces';

import { LOADING_SAVED_QUOTATIONS_STATES, PLAN_TYPE, QuotationStep } from '../../../../shared';
import { displayErrorMessage } from '../../../../../../../../shared/utils';
import { LOADING_RENEWAL_QUOTATION_STATES } from '../../../../shared/interfaces/loading-renewal-quotation-states-enum';
import { LoadQuotationToRenewResponse } from '../../../../shared/interfaces/load-renewal-quotation-response-interface';

const planTypeEquivalent: { A: boolean, C: boolean } = {
  A: true,
  C: false
};

export type StepTwoFormFields = {
  [key: `${'plan-tab' | 'province-tab' | 'location-tab'}-${number}`]: string;
};


export const StepTwoFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const [stepTwoForm] = Form.useForm<StepTwoFormFields>();
  
  const { 
    currentStep,
    quotationInfo,
    loadSaveQuotationState,
    setLoadSaveQuotationState,
    savedQuotation,
    loadingSavedQuotation,
    loadingRenewalQuotation,
    renewalQuotationState,
    setRenewalQuotationState,
    quotationToRenewal
  } = useContext(QuotationInfoContext);

  const { 
    setCanShowSpinner, 
    getLocalitiesPerProvince, 
    setPlansSelected, 
    updateFormsAfterRemoveTabs,
    removedTabIndexes,
    setRemovedTabIndexes,
    setUserCanDeleteTabs,
  } = useContext(GenericDataFormContext);

  const INITIAL_KEY_TAB = 1;
  // Cobertura de bien único: el backend del PDF solo toma getBien()
  const MAX_TABS = 1;
  
  const [activeKey, setActiveKey] = useState<number>(INITIAL_KEY_TAB);
  const [ keyTabToDelete, setKeyTabToDelete ] = useState<number | null>(null);
  
  const [theftInsuranceFormValues, setTheftInsuranceFormValues] = useState<StepTwoFormFields | null>(null);
  
  const [ plansByActivityList, setPlansByActivityList ] = useState<Array<Products>>([]);
  const [ planOptionsBySelectedPlan, setPlanOptionsBySelectedPlan ] = useState<Array<SelectOption>>([]);
  const [ planTypeSelected, setPlanTypeSelected ] = useState<PLAN_TYPE>(PLAN_TYPE.OPEN);
  const [ isPlanTypeADisabled, setIsPlanTypeADisabled ] = useState(false);
  const [ isPlanTypeCDisabled, setIsPlanTypeCDisabled ] = useState(false);
  
  const [ areTabActionsAllowed, setAreTabActionsAllowed ] = useState<boolean>(true);

  const [ showModal, setShowModal ] = useState<boolean>(false);
  const [tabs, setTabs] = useState<Array<any>>([]);

  const newTabIndex = useRef(INITIAL_KEY_TAB);

  const addNewTab = useCallback(() => {
    if (tabs.length < MAX_TABS) {
      const newActiveKey = ++newTabIndex.current
      const isTabActionEnabled = planTypeEquivalent[planTypeSelected ?? PLAN_TYPE.CLOSED];
  
      setTabs([...tabs, {
        label: <HeaderTabTheftInsurance id={newActiveKey} areTabActionsAllowed={isTabActionEnabled} />,
        children: <TabsPropertyDataTheftInsurance id={newActiveKey} />,
        key: newActiveKey,
        closable: false,
        forceRender: true
      }]);
      
      setActiveKey(newActiveKey);
      
      if(removedTabIndexes.includes(newActiveKey)) {
        setRemovedTabIndexes((prevValues) => prevValues.filter(el => el !== newActiveKey));
      };
  
      return
    };
  
    displayErrorMessage("Solo puede agregar 1 Robo (cobertura de bien único)");
  }, [
    tabs, 
    newTabIndex, 
    setTabs, 
    setActiveKey, 
    planTypeSelected,
    removedTabIndexes,
    setRemovedTabIndexes,
  ]);

  const duplicateTab = useCallback((id: number) => {
    if (tabs.length < MAX_TABS) {
      const tabSelected = tabs.find((tab: any) => tab.key === id)
      const newActiveKey = ++newTabIndex.current
      const isTabActionEnabled = planTypeEquivalent[planTypeSelected ?? PLAN_TYPE.CLOSED];

      if (tabSelected) {
        const newTab = {
          ...tabSelected,
          key: newActiveKey,
          label: <HeaderTabTheftInsurance id={newActiveKey} areTabActionsAllowed={isTabActionEnabled} />,
          children: <TabsPropertyDataTheftInsurance id={newActiveKey} duplicateId={id} />,
          closable: false,
          forceRender: true
        }
        setTabs([...tabs, newTab]);
        setActiveKey(newActiveKey);
        if(removedTabIndexes.includes(newActiveKey)) {
          setRemovedTabIndexes((prevValues) => prevValues.filter(el => el !== newActiveKey));
        };
      }
  
      return
    }
  
    displayErrorMessage("Solo puede agregar 1 Robo (cobertura de bien único)");
  }, [
    tabs, 
    newTabIndex, 
    setTabs, 
    setActiveKey, 
    planTypeSelected,
    removedTabIndexes,
    setRemovedTabIndexes,
  ]);

  const removeTab = useCallback((targetKey: number) => {
    setCanShowSpinner(true);
  
    setTimeout(() => {
      updateFormsAfterRemoveTabs([targetKey], [stepTwoForm]);

      setRemovedTabIndexes((prevValues) => [ ...prevValues, targetKey ]);
      const targetIndex = tabs.findIndex((pane: any) => pane.key === targetKey);
      const newPanes = tabs.filter((pane: any) => pane.key !== targetKey).map((tab, index) => ({
        ...tab,
        key: index + 1,
        label: <HeaderTabTheftInsurance id={index + 1} areTabActionsAllowed={areTabActionsAllowed}/>,
        children: <TabsPropertyDataTheftInsurance id={index + 1} />,
      }));
      if (newPanes.length && targetKey === activeKey) {
        const { key } = newPanes[targetIndex === newPanes.length ? targetIndex - 1 : targetIndex];
        setActiveKey(key);
      }
      newTabIndex.current = tabs.length - 1;
      setTabs(newPanes);
      setCanShowSpinner(false);
    }, 500);
  }, [tabs, setTabs, setActiveKey, activeKey]);

  const resetTabs = useCallback((unconfirmedPlanType: PLAN_TYPE) => {
    const isTabActionEnabled = planTypeEquivalent[unconfirmedPlanType];
    setTabs([
      { 
        label: <HeaderTabTheftInsurance id={INITIAL_KEY_TAB} areTabActionsAllowed={isTabActionEnabled} />, 
        children: <TabsPropertyDataTheftInsurance id={INITIAL_KEY_TAB} />, 
        key: INITIAL_KEY_TAB, 
        closable: false,
        forceRender: true
      }
    ]);

    stepTwoForm.resetFields();
    newTabIndex.current = INITIAL_KEY_TAB;
    setActiveKey(INITIAL_KEY_TAB);
  }, [tabs, setTabs, setActiveKey]);

  const getListPlansActivity = useCallback( 
    async (article: string, branch: string) => {
      if (plansByActivityList.length) return;

      setCanShowSpinner(true);
      try {
        const response = await QuotationService.getProductsListPerProducer(article, branch);
  
        if (response?.data?.productosPorProductor.length) {
          const productosPorProductor = response?.data?.productosPorProductor;
          
          setPlansByActivityList(productosPorProductor);
          setPlanTypeSelected(savedQuotation.tipoDePlan ?? (productosPorProductor.some(plan => plan.tipoDePlan === PLAN_TYPE.OPEN) ? PLAN_TYPE.OPEN : PLAN_TYPE.CLOSED));
        }
      } catch (error) {
      } finally {
        setCanShowSpinner(false);
      }
    }, [plansByActivityList.length, savedQuotation, setCanShowSpinner])

  const filterPlansByPlanTypeSelected = useCallback(() => {
    if (planTypeSelected) {
      const selectedPlanTypeValue: PLAN_TYPE = planTypeSelected;

      if (!theftInsuranceFormValues && !savedQuotation) {
        setTabs([
          { 
            label: <HeaderTabTheftInsurance id={INITIAL_KEY_TAB} areTabActionsAllowed={planTypeEquivalent[selectedPlanTypeValue]} />, 
            children: <TabsPropertyDataTheftInsurance id={INITIAL_KEY_TAB} />, 
            key: INITIAL_KEY_TAB, 
            closable: false,
            forceRender: true
          }
        ]);
      }
        
      setAreTabActionsAllowed(planTypeEquivalent[selectedPlanTypeValue]);
   
      const filteredPlans = plansByActivityList.filter(plan => plan.tipoDePlan === selectedPlanTypeValue);
        
      const plansOptions = filteredPlans.map((el) => ({
        label: `${el.codigo} - ${el.descripcionLarga}`,
        value: el.codigo
      }));
        
      setPlanOptionsBySelectedPlan(plansOptions);
    }
  }, [plansByActivityList, planTypeSelected]);

  useEffect(() => {
    if (!theftInsuranceFormValues && plansByActivityList.length && planTypeSelected) {
      filterPlansByPlanTypeSelected();
    }
  }, [filterPlansByPlanTypeSelected, planTypeSelected, plansByActivityList.length]);

  const fillStepTwoServices = useCallback(
    async (isRenewal: boolean) => {
      try {
        let quotationToProccess: LoadQuotationToRenewResponse;
        if (isRenewal) {
          quotationToProccess = quotationToRenewal;
        } else {
          quotationToProccess = savedQuotation;
        }
        const promises: Array<Promise<void>> = [
          getListPlansActivity(quotationInfo!.article, quotationInfo!.branch),
        ];
        const provincesSelected: Array<string> = [];
        const plansSelected: Array<string> = [];

        quotationToProccess.bienes.forEach((bien: any) => {
          if (!provincesSelected.includes(bien.provincia)) {
            provincesSelected.push(bien.provincia);
          }

          if (!plansSelected.includes(String(bien.planSeleccionado))) {
            plansSelected.push(String(bien.planSeleccionado));
          }
        });

        provincesSelected.forEach((province) =>
          promises.push(getLocalitiesPerProvince(province))
        );
        setPlansSelected(plansSelected);

        await Promise.all(promises).then(() => {
          if (isRenewal) {
            setRenewalQuotationState(
              LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADED_RENEWAL
            );
          } else {
            setLoadSaveQuotationState(
              LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADED
            );
          }
        });
      } catch (error) {}
    },
    [
      quotationInfo,
      savedQuotation,
      quotationToRenewal,
      getListPlansActivity,
      getLocalitiesPerProvince,
      setLoadSaveQuotationState,
      setPlansSelected,
    ]
  );

  const fillStepTwoData = useCallback(async (isRenewal: boolean) => {
      const _tabs: any = [];
      const _inputs: any = [];
      const isTabActionEnabled = planTypeEquivalent[planTypeSelected ?? PLAN_TYPE.CLOSED];

      let quotationToProccess: LoadQuotationToRenewResponse;
      if (isRenewal) {
        quotationToProccess = quotationToRenewal;
      } else {
        quotationToProccess = savedQuotation;
      }

      setUserCanDeleteTabs(false);

      quotationToProccess.bienes.forEach((good: any, index: number) => {
        const _activeKey = index + 1;

        _tabs.push({
          label: <HeaderTabTheftInsurance id={_activeKey} areTabActionsAllowed={isTabActionEnabled} />, 
          children: <TabsPropertyDataTheftInsurance id={_activeKey} />,
          key: _activeKey,
          closable: false,
          forceRender: true,
        });

        _inputs.push({ 
          name: `plan-tab-${_activeKey}`, 
          value: `${good.planSeleccionado}` 
        });
        _inputs.push({ 
          name: `province-tab-${_activeKey}`, 
          value: `${good.provincia}` 
        });
        _inputs.push({ 
          name: `location-tab-${_activeKey}`, 
          value: `${good.sufijoPostal}${good.codigoPostal}` 
        });
        _inputs.push({
          name: `propertyDataAddress${_activeKey}`,
          value: `${good.domicilio}`,
        });

      });

      newTabIndex.current = quotationToProccess.bienes.length;

      setTabs(_tabs);

      setActiveKey(INITIAL_KEY_TAB);

      stepTwoForm.setFields(_inputs);
      
    }, [
      savedQuotation,
      quotationToRenewal,
      setActiveKey,
      setTabs,
      stepTwoForm,
      planTypeSelected,
      setUserCanDeleteTabs,
    ]);

  useEffect(() => {
    if (savedQuotation) {
      setIsPlanTypeADisabled(true);
      setIsPlanTypeCDisabled(true);
    } else if (plansByActivityList.length) {
      const hasPlanTypeA = plansByActivityList.some(
        (plan) => plan.tipoDePlan === PLAN_TYPE.OPEN
      );

      const hasPlanTypeC = plansByActivityList.some(
        (plan) => plan.tipoDePlan === PLAN_TYPE.CLOSED
      );

      setIsPlanTypeADisabled(!hasPlanTypeA);
      setIsPlanTypeCDisabled(!hasPlanTypeC);
    }
  }, [plansByActivityList, savedQuotation]);

  useEffect(() => {
    if (!loadingSavedQuotation && quotationInfo && currentStep === QuotationStep.TWO) {
      getListPlansActivity(quotationInfo.article, quotationInfo.branch);
    }
  }, [quotationInfo, currentStep, setCanShowSpinner, loadingSavedQuotation, getListPlansActivity]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_ONE_DATA_FILLED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADING);
      fillStepTwoServices(false);
    }
  }, [loadSaveQuotationState]);
  
  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLING);
      fillStepTwoData(false);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLING && tabs.length === savedQuotation.bienes.length) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLED);
    }
  }, [
    loadSaveQuotationState,
    savedQuotation,
    tabs
  ]);

  /**
   * Renewal
   */

  useEffect(() => {
    if (quotationToRenewal) {
      setIsPlanTypeADisabled(true);
      setIsPlanTypeCDisabled(true);
    } else if (plansByActivityList.length) {
      const hasPlanTypeA = plansByActivityList.some(
        (plan) => plan.tipoDePlan === PLAN_TYPE.OPEN
      );

      const hasPlanTypeC = plansByActivityList.some(
        (plan) => plan.tipoDePlan === PLAN_TYPE.CLOSED
      );

      setIsPlanTypeADisabled(!hasPlanTypeA);
      setIsPlanTypeCDisabled(!hasPlanTypeC);
    }
  }, [plansByActivityList, quotationToRenewal]);

  useEffect(() => {
    if (!loadingRenewalQuotation && quotationInfo && currentStep === QuotationStep.TWO) {
      getListPlansActivity(quotationInfo.article, quotationInfo.branch);
    }
  }, [quotationInfo, currentStep, setCanShowSpinner, loadingRenewalQuotation, getListPlansActivity]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_ONE_DATA_FILLED_RENEWAL) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADING_RENEWAL);
      fillStepTwoServices(true);
    }
  }, [renewalQuotationState]);
  
  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADED_RENEWAL) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLING_RENEWAL);
      fillStepTwoData(true);
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLING_RENEWAL && tabs.length === quotationToRenewal.bienes.length) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLED_RENEWAL);
    }
  }, [
    renewalQuotationState,
    quotationToRenewal,
    tabs
  ]);

  const values = useMemo(
    () => ({
      stepTwoForm,
      activeKey,
      setActiveKey,
      tabs,
      setTabs,
      duplicateTab,
      removeTab,
      addNewTab,
      keyTabToDelete,
      setKeyTabToDelete,
      showModal,
      setShowModal,
      planOptionsBySelectedPlan,
      areTabActionsAllowed,
      planTypeSelected,
      setPlanTypeSelected,
      resetTabs,
      theftInsuranceFormValues,
      setTheftInsuranceFormValues,
      isPlanTypeADisabled,
      setIsPlanTypeADisabled,
      isPlanTypeCDisabled,
      setIsPlanTypeCDisabled,
    }),
    [
      stepTwoForm,
      activeKey,
      setActiveKey,
      tabs,
      setTabs,
      duplicateTab,
      removeTab,
      addNewTab,
      keyTabToDelete,
      setKeyTabToDelete,
      showModal,
      setShowModal,
      planOptionsBySelectedPlan,
      areTabActionsAllowed,
      planTypeSelected,
      setPlanTypeSelected,
      resetTabs,
      theftInsuranceFormValues,
      setTheftInsuranceFormValues,
      isPlanTypeADisabled,
      setIsPlanTypeADisabled,
      isPlanTypeCDisabled,
      setIsPlanTypeCDisabled,
    ]
  );

  return (
    <StepTwoFormContext.Provider value={values}>
      {children}
    </StepTwoFormContext.Provider>
  );
};